import { type ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { customerApi } from './customer_api';
import { useCustomerAuth } from './useCustomerAuth';

/**
 * Customer-side auth + waiver gate.
 *
 * 1. If not signed in → /sign-in.
 * 2. If the tenant has a waiver configured AND the customer's latest
 *    acceptance is below the tenant's current version → /account/waiver.
 * 3. Pass `skipWaiverCheck` to bypass step 2 (used by the waiver page
 *    itself so the user can sign without an infinite redirect).
 */
export function CustomerAuthGate({
  children,
  skipWaiverCheck = false,
}: {
  children: ReactNode;
  skipWaiverCheck?: boolean;
}) {
  const { loading, user } = useCustomerAuth();
  const [waiverChecked, setWaiverChecked] = useState(false);
  const [waiverRedirect, setWaiverRedirect] = useState(false);

  useEffect(() => {
    if (loading || !user || skipWaiverCheck) {
      setWaiverChecked(true);
      return;
    }
    customerApi<{ requiredVersion: number | null; currentAcceptedVersion: number | null }>(
      '/api/customer/waiver/status',
    )
      .then((res) => {
        if (res.requiredVersion !== null && res.currentAcceptedVersion !== res.requiredVersion) {
          setWaiverRedirect(true);
        }
      })
      .catch(() => {
        // If the status endpoint fails, fall through — the customer can
        // re-attempt and we shouldn't lock them out due to a 5xx.
      })
      .finally(() => setWaiverChecked(true));
  }, [loading, user, skipWaiverCheck]);

  if (loading || !waiverChecked) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ color: 'var(--muted-foreground)' }}>Loading…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/sign-in" replace />;
  if (waiverRedirect) return <Navigate to="/account/waiver" replace />;
  return <>{children}</>;
}
